import {
    createStyles,
    DialogActions as MuiDialogActions,
    makeStyles,
} from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import { Button, RelodeTheme } from "..";
import { useDeviceSize } from "../hooks";

const useDialogActionsStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        root: {
            marginRight: 16,
            marginLeft: 16,
            marginBottom: 16,
            marginTop: 8,
            [theme.breakpoints.down("sm")]: {
                display: "flex",
                flexDirection: "column",
            },
        },
        injectedRoot: {
            marginRight: theme.spacing(2),
            marginLeft: theme.spacing(2),
            marginTop: theme.spacing(1),
        },
        buttons: {
            [theme.breakpoints.down("sm")]: {
                "& > button:not(:last-child)": {
                    marginBottom: 8,
                },
            },
        },
    })
);
export type DialogActionsProps = {
    children?: React.ReactNode;
    isInjected?: boolean;
    submit?: {
        onSubmit: () => void;
        label?: React.ReactNode;
        disabled?: boolean;
        color?: "primary" | "secondary" | "default";
    };
};

export const DialogActions = (props: DialogActionsProps) => {
    const { submit, isInjected = false } = props;
    const classes = useDialogActionsStyles(props);
    const device = useDeviceSize();
    const dataCy = props["data-cy"] ?? "dialogActions";

    if (!!submit && !!props.children) {
        throw new Error(
            "cannot use both submit prop and children prop on DialogActions"
        );
    }

    const renderChildren = () => {
        const _c = React.Children.map(
            props.children,
            (child: any, index: number) => {
                if (!child) return null;
                const _d =
                    child.props["data-cy"] ??
                    `${child.props.id}` ??
                    `action${index}`;

                return React.cloneElement(child, {
                    ...child.props,
                    "data-cy": `${dataCy}-${_d}`,
                });
            }
        );
        return _c;
    };

    const renderButtons = () => {
        if (!!submit) {
            return (
                <DialogActions.Button
                    data-cy={`${dataCy}-submit-button`}
                    id="dialogSubmitButton"
                    onClick={submit.onSubmit}
                    label={submit.label ?? "submit"}
                    disabled={submit.disabled}
                    color={submit.color ?? "primary"}
                />
            );
        } else {
            return <>{renderChildren()}</>;
        }
    };
    return (
        <MuiDialogActions
            disableSpacing={device === "mobile"}
            className={
                isInjected
                    ? classnames(classes.injectedRoot, classes.buttons)
                    : classnames(classes.root, classes.buttons)
            }
            data-cy={`${dataCy}-actions`}
        >
            {renderButtons()}
        </MuiDialogActions>
    );
};

const useDialogActionButtonStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                margin: 0,
            },
        }),
    { name: "DialogActionsButton" }
);

type DialogActionButtonProps = {
    label: React.ReactNode;
    onClick: () => void;
    id?: string;
    color?: "primary" | "secondary" | "default";
    disabled?: boolean;
};

const DialogActionButton = (props: DialogActionButtonProps) => {
    const { label, onClick, id, color, disabled, ...rest } = props;
    const classes = useDialogActionButtonStyles(props);
    const device = useDeviceSize();
    const dataCy = props["data-cy"] ?? `${props.id}-button`;
    return (
        <Button
            id={id}
            className={classes.root}
            fullWidth={device === "mobile"}
            size="small"
            color={color}
            variant="contained"
            disabled={disabled}
            onClick={onClick}
            {...rest}
            data-cy={dataCy}
        >
            {label}
        </Button>
    );
};

DialogActions.Button = DialogActionButton;
